import { createSlice } from "@reduxjs/toolkit";
import { Constants } from "@constants/Constants";
import apiService from "@utils/apiService";
import { verifyApiResponse } from "@utils/apiResponseHelper";
import { blobDownload } from "@utils/utilities";

const initialState = {
  scorecardData: {},
  scorecardUseCases: [],
  printingScorecard: false,
  scorecardUseCaseFailure: false,
  scorecardUseCaseFailureCode: null,
  isLoading: false,
  pagination: {
    page_num: 0,
    page_size: 5,
  },
  toasterVariant: 'success',
  toasterMessage: '',
  toasterOpen: false,
  sideDrawer: false,
};

export const scorecardSlice = createSlice({
  name: "scorecard",
  initialState,
  reducers: {
    setScorecardData: (state, action) => {
      state.scorecardData = action.payload;
    },
    setScorecardUseCases: (state, action) => {
      state.scorecardUseCases = action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setScorecardUseCaseFailure: (state, action) => {
      state.scorecardUseCaseFailure = true;
      state.scorecardUseCaseFailureCode = action.payload;
    },
    closeToaster: (state) => {
      state.toasterOpen = false;
      state.toasterMessage = initialState.toasterMessage;
      state.toasterVariant = initialState.toasterVariant;
    },
    openToaster: (state, action) => {
      state.toasterOpen = true;
      state.toasterMessage = action.payload.message;
      state.toasterVariant = action.payload.variant;
    },
    updateSideDrawer: (state, action) => {
      state.sideDrawer = action.payload;
    },
    setPrintingScorecard: (state, action) => {
      state.printingScorecard = action.payload;
    },
  },
});

const { actions, reducer } = scorecardSlice;
export const {
  setScorecardUseCases,
  setScorecardData,
  setIsLoading,
  setPagination,
  setScorecardUseCaseFailure,
  setChartUseCaseIds,
  closeToaster,
  openToaster,
  updateSideDrawer,
  setPrintingScorecard,
} = actions;

const toggleErrorToaster = (dispatch) => {
  dispatch(openToaster({
    variant: 'error',
    message: Constants.USECASE_DETAILS.TOASTER_MESSAGE.FAILURE_MESSAGE
  }));
}

export const toggleUseCaseToChart = (data) => (dispatch, getState) => {
  const {
    isAddedToGraph, scorecardId, useCaseid, scorecardUseCases = []
  } = data || {};

  apiService.put(
    `${Constants.API.USE_CASE_DETAIL}/${useCaseid}${Constants.API.SCORE_CARD_TERM}${scorecardId}${Constants.API.SCORE_CARD_TOGGLE_TO_GRAPH}`, {}, {
    params: {
      isAddedToGraph: isAddedToGraph,
    }
  }).then((response) => {
    let data = verifyApiResponse(response);
    if (data?.systemMessage?.success) {
      dispatch(openToaster({
        variant: isAddedToGraph ? 'success' : 'info',
        message: isAddedToGraph ? Constants.SCORECARD.USE_CASE_ADD_TO_GRAPH : 
          Constants.SCORECARD.USE_CASE_REMOVED_FROM_GRAPH
      }));
      dispatch(
        setScorecardUseCases(
          scorecardUseCases.map(item => {
            if (item.id === useCaseid) {
              return {
                ...item,
                isAddedToGraph: isAddedToGraph,
              }
            }
            return item;
          })
        )
      );
    } else {
      toggleErrorToaster(dispatch);
    }
  }).catch(() => {
    toggleErrorToaster(dispatch);
  });
};

export const loadScorecardUseCases =
  (scorecardId, pagination = initialState.pagination, filtersPayload = Constants.SCORECARD_FILTER_DRAWER.FILTERS_PAYLOAD) =>
    (dispatch) => {
      dispatch(setPagination(pagination));
      dispatch(setIsLoading(true));
      return apiService
        .post(`${Constants.API.SCORE_CARD}/${scorecardId}`,
          filtersPayload, 
          {
            params: {
              page_num: 0,
              page_size: 100,
            },
          }
        )
        .then((response) => {
          const { useCases = [] } = response.data?.data || {};
          dispatch(setScorecardUseCases(useCases.filter(useCase => useCase.title)));
          dispatch(setScorecardData(response.data?.data || {}));
        })
        .catch((error) => {
          dispatch(setScorecardUseCaseFailure(error?.response?.status || 'error'));
        })
        .finally(() => {
          dispatch(setIsLoading(false));
        });
    };

export const editUseCase = (usecase, data) => async (dispatch) => {
  return await apiService.put(
    `${Constants.API.SCORE_CARD}/${data.scorecardId}${Constants.API.USE_CASE_TERM}`
  , usecase).then(() => {
    dispatch(openToaster({
      variant: 'success',
      message: Constants.SCORECARD.USE_CASE_EDITED,
    }))
    dispatch(loadScorecardUseCases(data.scorecardId, data.pagination));
    return { success: true }
  }).catch(() => {
    dispatch(openToaster({
      variant: 'error',
      message: Constants.USECASE_DETAILS.TOASTER_MESSAGE.FAILURE_MESSAGE,
    }));
    return { success: false }
  });
  
};

export const exportScorecardInExcel = (scorecardId, filtersPayload) => async () => {
  return apiService
    .post(
      `${Constants.API.SCORE_CARD}/${scorecardId}/${Constants.API.SCORE_CARD_EXPORT_EXCEL}`,
      filtersPayload,
      {
        responseType: "blob",
      }
    )
    .then((response) => {
      const contentDisposition = response.headers["content-disposition"];
      const parts = contentDisposition.split("filename=");
      const filename = parts[1] ? parts[1].trim() : null;
      blobDownload(
        response.data,
        filename || Constants.SCORECARD.EXPORT_FILE_NAME
      );
    })
}

export const dismissWeightsBanner = (scorecardId) => async (dispatch) => {
  return await apiService.delete(
    `${Constants.API.CUSTOMIZE_WEIGHTS}${Constants.API.SCORE_CARD_CALLOUT}${scorecardId}`
  ).then(() => {
    return { dismiss: true }
  }).catch(() => {
    toggleErrorToaster(dispatch);
    return { dismiss: false }
  });
}

export default reducer;
